import { useState } from 'react';
import { Form } from 'react-router';
import { actions, events, type OAuth2Provider, providers, views } from '@cx-web/feature-auth';
import { trackEvent } from '@cx-web/feature-tracking/dataLayer';
import { SocialButton } from '@fpsm/cxy-components/SocialButton';

import { stripAuthModalDeepLinkParams, type View, VIEW_SIGNUP } from '../../../auth-modal-provider';

import styles from './styles/SocialButtons.module.css';

export type SocialButtonsProps = {
  authBasePath?: string;
  currentUrl: string;
  googleID: string;
  marketingConsent?: boolean;
  repubPath: string;
  view: View;
};

export const SocialButtons = ({
  authBasePath = '/plus/auth',
  currentUrl,
  googleID,
  marketingConsent,
  repubPath,
  view,
}: SocialButtonsProps) => {
  const [isLoading, setIsLoading] = useState(false);

  // currentUrl is the SSR prop, so it still carries auth_view / oc_prefill_email after the host's
  // address-bar strip — keep them out of the 302 Location, returnTo cookie and redirect logs.
  const returnTo = stripAuthModalDeepLinkParams(currentUrl);

  const renderFormWithSocialButton = (provider: OAuth2Provider) => {
    if (provider === providers.GOOGLE && !googleID) return null;

    const action = `${repubPath}${authBasePath}/${provider}`;

    const inputView = view === VIEW_SIGNUP ? views.SIGN_UP : views.LOGIN;

    return (
      <Form
        action={action}
        method="post"
        // OAuth always ends in a cross-origin redirect (accounts.google.com). A JS-intercepted
        // submission — react-router's own fetcher, or a custom action shimming one (see
        // atlas-astro's AuthModalDriver) — dispatches this via fetch(), and a fetch() redirect
        // into a different origin is subject to CORS: the browser blocks reading the response,
        // surfacing as a bare "Failed to fetch" with no indication anything even reached Google.
        // reloadDocument makes this one form submit as a genuine native browser POST, bypassing
        // react-router's JS layer entirely — the ensuing 302 is then a real top-level navigation,
        // which isn't subject to CORS the way fetch/XHR redirects are. Every other form in this
        // package (login/signup/forgot-password) stays JS-submitted; only this one ever redirects
        // cross-origin.
        onSubmit={() => {
          setIsLoading(true);
          trackEvent(events.SOCIAL_ATTEMPT, {
            eventAction: actions.SOCIAL_AUTH,
            method: provider,
            view: inputView,
          });
        }}
        reloadDocument
      >
        <input key="returnTo" name="returnTo" type="hidden" value={returnTo} />
        <input key="view" name="view" type="hidden" value={inputView} />
        {marketingConsent !== undefined && (
          <input key="marketingConsent" name="marketingConsent" type="hidden" value={String(marketingConsent)} />
        )}
        <SocialButton className={styles.socialButton} isLoading={isLoading} variant={provider} />
      </Form>
    );
  };

  return <div data-testid="social-buttons">{renderFormWithSocialButton(providers.GOOGLE)}</div>;
};

SocialButtons.displayName = 'SocialButtons';
